import React, { useState, useRef, useEffect } from "react";
import Icon from "Components/Icons/Icon";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "Components/Hooks/hooks";
import { restartTour } from "slices/tour/reducer";
import ChecklistCard from "./ChecklistCard";

const FloatingSetupGuide: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  // const { completedTourGuides = [] } = useAppSelector((state) => state.auth);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isOpen &&
        cardRef.current &&
        buttonRef.current &&
        !cardRef.current.contains(e.target as Node) &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const toggleCard = (): void => {
    setIsOpen(!isOpen);
  };

  const handleTourNavigation = (id: number): void => {
    setIsOpen(false);
    switch (id) {
      case 1:
        dispatch(restartTour("dashboard"));
        if (location.pathname !== "/dashboard") {
          navigate("/dashboard");
        }
        break;
      case 2:
        dispatch(restartTour("quoteSummary"));
        dispatch(restartTour("quoteItems"));
        dispatch(restartTour("quoteSections"));
        if (location.pathname !== "/manage-quotes") {
          navigate("/manage-quotes");
        }
        break;
      case 3:
        dispatch(restartTour("invoiceSummary"));
        dispatch(restartTour("invoiceItems"));
        if (location.pathname !== "/manage-invoice") {
          navigate("/manage-invoice");
        }
        break;
      case 4:
        dispatch(restartTour("presetQuote"));
        if (location.pathname !== "/preset-quotes") {
          navigate("/preset-quotes");
        }
        break;
      case 5:
        dispatch(restartTour("settings"));
        if (location.pathname !== "/settings") {
          navigate("/settings");
        }
        break;

      default:
        break;
    }
  };

  const styles = {
    floatingBtn: {
      position: "fixed" as const,
      bottom: "30px",
      right: "30px",
      width: "56px",
      height: "56px",
      borderRadius: "50%",
      border: "none",
      boxShadow: "0 4px 12px rgba(64, 81, 137, 0.4)",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s ease",
      cursor: "pointer",
    },
    floatingCard: {
      position: "fixed" as const,
      bottom: "80px",
      right: "30px",
      width: "320px",
      zIndex: 999,
      animation: "setupGuideSlideUp 0.3s ease-out",
      // boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
      border: "1px solid #e9ebec",
      borderRadius: "12px",
    },
  };

  return (
    <>
      <style>{`
        @keyframes setupGuideSlideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .setup-guide-floating-btn:hover {
          transform: scale(1.1) !important;
          box-shadow: 0 6px 16px rgba(64, 81, 137, 0.5) !important;
        }
        
        .setup-guide-close-btn:hover {
          color: #405189 !important;
        }
      `}</style>

      {isOpen && (
        <div style={styles.floatingCard} ref={cardRef} className="br-12">
          <ChecklistCard onCardPress={handleTourNavigation} />
        </div>
      )}

      <button
        type="button"
        ref={buttonRef}
        className="d-flex align-items-center justify-content-center gap-2 border-0 btn-press-effect bg-primary"
        style={styles.floatingBtn}
        onClick={toggleCard}
      >
        <Icon name={isOpen ? "TourClose" : "TourInfo"} />
      </button>
    </>
  );
};

export default React.memo(FloatingSetupGuide);
